import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { bulkSaveAssets, createAsset, deleteAsset, listAssets } from '../api/assets';

function groupByType(list) {
  return {
    item:      list.filter((a) => a.type === 'item'),
    container: list.filter((a) => a.type === 'container'),
    pallet:    list.filter((a) => a.type === 'pallet'),
  };
}

export function useAssets() {
  const { user } = useAuth();
  const token  = user?.token;
  const userId = user?.id;

  const [all,     setAll]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await listAssets(token, userId);
      setAll(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => { load(); }, [load]);

  const add = async (type, name, data) => {
    const asset = await createAsset({ type, name, data }, token, userId);
    setAll((prev) => [...prev, asset]);
    return asset;
  };

  const remove = async (id) => {
    await deleteAsset(id, token, userId);
    setAll((prev) => prev.filter((a) => a._id !== id));
  };

  // Upsert after a simulation run. Silently ignores errors (non-blocking).
  const saveFromSimulation = async (items) => {
    if (!token || !items?.length) return;
    try {
      const saved = await bulkSaveAssets(items, token, userId);
      // Merge saved into local state (update existing, append new)
      setAll((prev) => {
        const map = new Map(prev.map((a) => [a._id, a]));
        saved.forEach((a) => map.set(a._id, a));
        return Array.from(map.values());
      });
    } catch {
      // silent — asset saving is a best-effort side effect
    }
  };

  return {
    assets:  groupByType(all),
    all,
    loading,
    error,
    reload:  load,
    add,
    remove,
    saveFromSimulation,
  };
}
